import { useEffect } from 'react';
import { Download, RotateCcw, Clock, User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useGetRequest, usePostRequest } from '@/shared/hooks/requests.js';
import { FILE_VERSIONS, FILE_VERSION_CONTENT, FILE_VERSION_RESTORE } from '@/shared/utils/urls.js';
import { downloadBlob, fetchAuthedBlob, versionFileName } from '@/shared/utils/authedFetch.js';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';

export const VersionHistoryModal = ({ open, onOpenChange, file, setData }) => {
  const { t, i18n } = useTranslation(['common', 'version']);

  // Locale-aware date/time — a hardcoded 'MMM D, YYYY HH:mm' pattern always
  // rendered English month names regardless of the active language.
  const formatVersionDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getVersions = useGetRequest({
    url: FILE_VERSIONS.replace('{file_uuid}', file?.uuid),
  });

  const restoreVersion = usePostRequest({});

  useEffect(() => {
    if (open && file?.uuid) {
      getVersions.request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.uuid]);

  // With the session cookie, never a token in the URL (see
  // VersionHistoryPanel).
  const handleDownload = async (version) => {
    const path = FILE_VERSION_CONTENT.replace('{file_uuid}', file.uuid).replace('{version}', version.versionNumber);
    try {
      downloadBlob(await fetchAuthedBlob(path), versionFileName(file.name, version.versionNumber));
    } catch (err) {
      toast.error(err.detail?.message || t('editor:downloadFailed', 'Download failed'));
    }
  };

  const handleRestore = async (version) => {
    const url = FILE_VERSION_RESTORE
      .replace('{file_uuid}', file.uuid)
      .replace('{version}', version.versionNumber);

    const { success } = await restoreVersion.request({ url });

    if (success) {
      toast.success(t('version:history.restoredAs', { n: version.versionNumber }));
      getVersions.request(); // Refresh the list
      setData?.();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const versions = getVersions.response?.versions || [];
  const currentVersion = getVersions.response?.currentVersion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2 text-foreground">
            <Clock className="h-5 w-5 text-primary" />
            {t('version:history.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-md border border-border/50">
            <div className="flex items-center gap-3 min-w-0 pr-4">
              <div className="p-2 bg-primary/10 rounded-md">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('version:history.file')}
                </span>
                <span className="text-sm font-medium text-foreground truncate max-w-[300px]" title={file?.name}>
                  {file?.name}
                </span>
              </div>
            </div>
            {currentVersion && (
              <Badge variant="outline" className="border-primary/20 text-primary bg-primary/5 px-2 py-1">
                {t('version:history.currentVersion')}: v{currentVersion}
              </Badge>
            )}
          </div>

          {getVersions.loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner className="w-8 h-8" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-md border border-dashed border-border">
              {t('version:history.noVersions')}
            </div>
          ) : (
            <div className="max-h-96 overflow-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="w-[120px] text-foreground font-semibold">
                      {t('version:history.version')}
                    </TableHead>
                    <TableHead className="w-[180px] text-foreground font-semibold">
                      {t('version:history.date')}
                    </TableHead>
                    <TableHead className="w-[140px] text-foreground font-semibold">
                      {t('version:history.author')}
                    </TableHead>
                    <TableHead className="w-[100px] text-foreground font-semibold">
                      {t('version:history.size')}
                    </TableHead>
                    <TableHead className="text-foreground font-semibold">
                      {t('version:history.note')}
                    </TableHead>
                    <TableHead className="text-right w-[100px] text-foreground font-semibold">
                      {t('version:history.actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={version.uuid} className="border-border hover:bg-muted/50 transition-colors">
                      <TableCell className="font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          <span className="text-primary font-bold">v{version.versionNumber}</span>
                          {version.isCurrent && (
                            <Badge className="bg-green-500/10 text-green-600 border border-green-500/20 text-[10px] px-1.5 py-0 h-4">
                              {t('version:history.current')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatVersionDate(version.updatedAt || version.createdAt)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm truncate max-w-[140px]" title={version.authorName}>
                        {version.authorName || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatFileSize(version.sizeBytes)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-40 truncate overflow-hidden" title={version.versionNote}>
                        {version.versionNote || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(version)}
                            title={t('version:history.download')}
                            className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {!version.isCurrent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestore(version)}
                              disabled={restoreVersion.loading}
                              title={t('version:history.restore')}
                              className="text-muted-foreground hover:text-amber-600 hover:bg-amber-600/10"
                            >
                              {restoreVersion.loading ? (
                                <LoadingSpinner className="w-4 h-4" />
                              ) : (
                                <RotateCcw className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
